import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useMedplum } from "@medplum/react";

import { Accordion, Flex, Paper, Stack, Title } from "@mantine/core";

import { IconNotes } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import {
  clearGynecologicalDiffCache,
  getGynecologicalPhysicalExamDiffHistory,
  GynecologicalPhysicalExamDiffResult,
} from "@/physical-exams/services/gynecological-physical-exam-diff";

import GynecologicalPhysicalExamsDiffGrid from "./GynecologicalPhysicalExamsDiffGrid";

const GynecologicalPhysicalExamsDiff = ({
  currentVersionIndex,
  versionId,
}: {
  currentVersionIndex: number;
  versionId?: string;
}) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetching, setIsFetching] = useState(true);
  const [histories, setHistories] = useState<GynecologicalPhysicalExamDiffResult>({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsFetching(true);
      try {
        // Get the encounter to pass to the service
        const encounter = await medplum.readResource("Encounter", encounterId!);

        // Use the gynecological-specific service that returns the correct format
        const histories = await getGynecologicalPhysicalExamDiffHistory(
          medplum,
          encounter,
          versionId,
          currentVersionIndex,
        );

        if (mounted) {
          setHistories(histories);
          setIsFetching(false);
        }
      } catch (error) {
        console.error("Error fetching gynecological physical exam diff:", error);
        if (mounted) {
          setIsFetching(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [currentVersionIndex, encounterId, medplum, versionId]);

  // Clear cache when component unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      if (encounterId) {
        clearGynecologicalDiffCache(encounterId);
      }
    };
  }, [encounterId]);

  if (isFetching) return <Loading />;

  // Check if there's any physical exam data in either current or previous versions
  const hasAnyData = Object.values(histories).some((history) => {
    // History is now zipped data: [[current, previous], [current, previous], ...]
    return history.length > 0;
  });

  if (!hasAnyData) {
    return (
      <Paper p="lg" withBorder>
        <Title mb="lg" order={2}>
          Physical Exams
        </Title>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No physical exam data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack>
        <Title mb="lg" order={3}>
          Physical Exams
        </Title>
        {Object.keys(histories).map((title) => {
          return (
            <Accordion
              multiple
              chevronPosition="right"
              key={`${title}-accordion`}
              data-testid="physical-exams-accordion"
              styles={{
                label: { padding: 0 },
                content: { padding: 0 },
              }}
            >
              <Accordion.Item
                value={title}
                key={`${title}-item`}
                style={{ border: "1px solid var(--mantine-color-cream-4)", borderRadius: 4 }}
              >
                <Accordion.Control
                  p="lg"
                  data-testid={`gynecological-physical-exam-diff-${title}-toggle`}
                >
                  <Flex justify="space-between">
                    <Flex align="center">
                      <IconNotes size={24} color="#a0a0a0" style={{ marginRight: 8 }} />
                      <TextWrapper
                        c="charcoal.0"
                        variant="bold"
                        aria-label={`Physical Exam ${title}`}
                      >
                        {title}
                      </TextWrapper>
                    </Flex>
                  </Flex>
                </Accordion.Control>
                <Accordion.Panel>
                  {histories !== undefined && (
                    <GynecologicalPhysicalExamsDiffGrid title={title} histories={histories} />
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default GynecologicalPhysicalExamsDiff;
